/**
 * Wraps functions in a constant to avoid declaration conflicts
 */
const IcrasHelpers = {
  // Message translations

  /**
   * Get label by key from global plugin object
   *
   * @param {string} key Message key
   *
   * @return {string}
   */
  label: (key) => {
    return window?.Icras?.labels[key] ?? null;
  },

  /**
   * Get message by key from global plugin object
   *
   * @param {string} type Message type: 'errors', 'warnings', 'success', 'labels'
   * @param {string} key Message key
   *
   * @return {string}
   */
  message: (type, key) => {
    let list = window?.Icras?.messages[type] ?? null;
    return list && (list[key] ?? null) ? list[key] : key;
  },

  /**
   * Get error message by key from global plugin object
   *
   * @param {string} key Message key
   *
   * @return {string}
   */
  error: (key) => {
    return IcrasHelpers.message('errors', key);
  },

  /**
   * Get warning message by key from global plugin object
   *
   * @param {string} key Message key
   *
   * @return {string}
   */
  warning: (key) => {
    return IcrasHelpers.message('warnings', key);
  },

  /**
   * Get success message by key from global plugin object
   *
   * @param {string} key Message key
   *
   * @return {string}
   */
  success: (key) => {
    return IcrasHelpers.message('success', key);
  },

  // Unit conversions

  /**
   * Convert a value to another unit
   *
   * NOTICE: only convert mmol/L <=> mg/dL
   *
   * @param {string} unit 'mg_dl' | 'mmol_l'
   * @param {string} type 'ch' (cholesterol) | 'tg' (triglyceride)
   * @param {number} value Value to convert
   *
   * @return {number|boolean} New value or false if an error occured
   */
  unitConvert: (unit, type, value) => {
    try {
      let conv = window.Icras.lab.conv[type];

      switch (unit) {
        case 'mg_dl':
          value *= conv;
          break;
        case 'mmol_l':
          value /= conv;
          break;
      }

      return Number(value.toFixed(2));
    } catch (error) {
      console.error('ICRAS - Error unitConvert: ' + error);
    }

    return false;
  },

  // Toggle element

  /**
   * Get Toggle component value
   *
   * @param {HTMLLabelElement} element Toggle component
   *
   * @return {string|null}
   */
  getToggleValue(element) {
    return (
      element
        .querySelector('input[type="checkbox"]:checked')
        ?.value.toString() || null
    );
  },

  /**
   * If Toggle component is checked
   *
   * @param {HTMLLabelElement} element Toggle component
   *
   * @return {bool}
   */
  isToggleChecked(element) {
    return IcrasHelpers.getToggleValue(element) === '1';
  },

  // Alerts

  /**
   * Build alert
   *
   * @param {Array<string>} messages Message list
   * @param {string} classNames List classnames
   *
   * @return {?HTMLUListElement}
   */
  buildAlert(messages, classNames = '') {
    if (messages.length <= 0) {
      return null;
    }

    let list = document.createElement('ul');
    list.classList.add(...classNames.split(' '));
    messages.forEach((msg) => {
      let li = document.createElement('li');
      li.innerText = msg;
      list.appendChild(li);
    });

    return list;
  },

  /**
   * @param {Array<string>} errors Error messages
   *
   * @return {?HTMLUListElement}
   */
  buildAlertError(errors) {
    return IcrasHelpers.buildAlert(errors, 'icras-errors');
  },

  /**
   * @param {Array<string>} warnings Warning messages
   *
   * @return {?HTMLUListElement}
   */
  buildAlertWarning(warnings) {
    return IcrasHelpers.buildAlert(warnings, 'icras-warnings');
  },

  /**
   * @param {Array<string>} success Success messages
   *
   * @return {?HTMLUListElement}
   */
  buildAlertSuccess(success) {
    return IcrasHelpers.buildAlert(success, 'icras-successes');
  },

  // Popin

  /**
   * @param {string} content Popin content
   * @param {Array<string>} errors Error messages
   * @param {Array<string>} warnings Warning messages
   * @param {Array<string} success Success messages
   *
   * @return {string}
   */
  buildPopinContent: (
    content = '',
    errors = [],
    warnings = [],
    success = []
  ) => {
    // wrapper
    let wrapper = document.createElement('div');

    // content
    if (content) {
      let contentWrapper = document.createElement('div');
      contentWrapper.classList.add('icras-popin-content');
      contentWrapper.innerHTML = content;
      wrapper.appendChild(contentWrapper);
    }

    let list = null;

    // warnings
    list = IcrasHelpers.buildAlertWarning(warnings);
    if (list) {
      wrapper.appendChild(list);
    }

    // errors
    list = IcrasHelpers.buildAlertError(errors);
    if (list) {
      wrapper.appendChild(list);
    }

    // success
    list = IcrasHelpers.buildAlertSuccess(success);
    if (list) {
      wrapper.appendChild(list);
    }

    return wrapper.innerHTML;
  },
};

export default IcrasHelpers;
